import React, { useState, useEffect } from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { Add, Remove } from '@mui/icons-material';

const normalizePlanSelectValue = (type) => {
  const t = (type || '').toLowerCase();
  if (!t || t === 'free') return '';
  return 'premium';
};

const UsageModal = ({ open, handleClose, userType, initialUsage, onSave }) => {
  // NOTE: The following fields were commented out due to recent functionality changes.
  // Keeping them here (not deleted) in case they need to be restored later.
  // Commented out: "Job Broadcasting Credits", "Job Broadcasting + Agentic", "Gmeet/Teams",
  // "Vendor Marketplace Job" (vendor) and "Vendor Only Jobs" (client).
  const vendorFields = [
    "User Accounts",
    // "Job Broadcasting Credits",
    // "Job Broadcasting + Agentic",
    "Intellica Credits",
    "AI Screening Credits",
    "Database Credits",
    // "Gmeet/Teams",
    // "Vendor Marketplace Job",
    "RMS",
  ];
  const clientFields = [
    "User Accounts",
    // "Job Broadcasting Credits",
    // "Job Broadcasting + Agentic",
    "Intellica Credits",
    "AI Screening Credits",
    "Database Credits",
    // "Gmeet/Teams",
    // "Vendor Only Jobs",
    "VMS",
  ];
  const fields = userType === 'vendor' ? vendorFields : clientFields;
  
  const [loading, setLoading] = useState(false);
  const [fieldValues, setFieldValues] = useState(
    fields.reduce((acc, field) => ({
      ...acc,
      [field]:
        field === "VMS" || field === "RMS"
          ? ""
          : field === "Gmeet/Teams"
          ? true
          : field === "Vendor Only Jobs"
          ? 0
          : 0
    }), {})
  );

  useEffect(() => {
    if (open && initialUsage) {
      setFieldValues({
        "User Accounts": initialUsage.users || 0,
        "Job Broadcasting Credits": initialUsage.jobPosting || 0,
        "Intellica Credits": initialUsage.aiInterviewCredits || 0,
        "AI Screening Credits": initialUsage.aiScreeningCredits || 0,
        "Job Broadcasting + Agentic": initialUsage.premiumJobCredits || 0,
        "Gmeet/Teams": initialUsage.interviewIntegration || false,
        "Database Credits": initialUsage.databaseCredits ?? 0,
        ...(userType === "vendor" ? { 
          "Vendor Marketplace Job": initialUsage.vendorJob || false,
          "RMS": normalizePlanSelectValue(initialUsage.rms?.type),
        } : {}),
        ...(userType === "client" ? { 
          "Vendor Only Jobs": initialUsage.vendorOnlyJobs || 0,
          "VMS": normalizePlanSelectValue(initialUsage.vms?.type),
        } : {})
      });
    }
  }, [open, initialUsage, userType]);

  // Update Vendor Only Jobs when VMS selection changes
  useEffect(() => {
    if (userType === 'client' && fieldValues["VMS"]) {
      const vmsType = fieldValues["VMS"];
      let vendorOnlyJobs = 0;
      
      // Only auto-populate if no manual value has been set
      const currentVendorOnlyJobs = fieldValues["Vendor Only Jobs"];
      const isDefaultValue = [0, 10, 25, 50, "unlimited"].includes(currentVendorOnlyJobs);
      
      if (isDefaultValue) {
        switch (vmsType) {
          case "basic":
            vendorOnlyJobs = 10;
            break;
          case "advance":
            vendorOnlyJobs = 25;
            break;
          case "premium":
            vendorOnlyJobs = 50;
            break;
          case "premiumplus":
            vendorOnlyJobs = "unlimited";
            break;
          default:
            vendorOnlyJobs = 0;
        }
        
        setFieldValues(prev => ({
          ...prev,
          "Vendor Only Jobs": vendorOnlyJobs
        }));
      }
    }
  }, [fieldValues["VMS"], userType]);

  const handleChange = (field, delta) => {
    if (field === "VMS" || field === "RMS" || 
        field === "Gmeet/Teams" ) return;
    
    if (field === "Vendor Only Jobs") {
      if (userType === 'client' && fieldValues["VMS"] !== "premiumplus") {
        setFieldValues((prev) => ({
          ...prev,
          [field]: Math.max(0, (prev[field] || 0) + delta),
        }));
      }
      return;
    }

    setFieldValues((prev) => ({
      ...prev,
      [field]: Math.max(0, (prev[field] || 0) + delta),
    }));
  };
  const handleSubmit = () => {
    setLoading(true);
    try {
      const data = {
        usage: {
          jobPosting: fieldValues["Job Broadcasting Credits"],
          users: fieldValues["User Accounts"],
          aiInterviewCredits: fieldValues["Intellica Credits"],
          aiScreeningCredits: fieldValues["AI Screening Credits"],
          premiumJobCredits: fieldValues["Job Broadcasting + Agentic"],
          interviewIntegration: fieldValues["Gmeet/Teams"],
          databaseCredits: fieldValues["Database Credits"],
          vendorOnlyJobs: userType === 'vendor' ? fieldValues["Vendor Marketplace Job"] : fieldValues["Vendor Only Jobs"],
        },
        ...(userType === "vendor" && {
          planType: fieldValues["RMS"] && fieldValues["RMS"] !== 'free' ? fieldValues["RMS"].toUpperCase() : null,
      }),
      ...(userType === "client" && {
          planType: fieldValues["VMS"] && fieldValues["VMS"] !== 'free' ? fieldValues["VMS"].toUpperCase() : null,
      })
      };
      
      onSave(data);
      handleClose();
    } catch (error) {
      console.error("Error processing usage data:", error);
    } finally {
      setLoading(false);
    }
  };

  const renderFieldControl = (field) => {
    if (field === "Gmeet/Teams") {
      return (
        <div className="flex justify-end items-center gap-2 w-2/3">
          <div className="flex items-center">
            <RadioGroup
              row
              value={fieldValues[field] ? "yes" : "no"}
              onChange={(e) => setFieldValues(prev => ({
                ...prev,
                [field]: e.target.value === "yes"
              }))}
              sx={{ flexWrap: 'nowrap' }}
            >
              <FormControlLabel
                value="yes"
                control={
                  <Radio
                    sx={{
                      '&.Mui-checked': {
                        color: '#3b82f6',
                      },
                    }}
                  />
                }
                label="Yes"
              />
              <FormControlLabel
                value="no"
                control={
                  <Radio
                    sx={{
                      '&.Mui-checked': {
                        color: '#3b82f6',
                      },
                    }}
                  />
                }
                label="No"
              />
            </RadioGroup>
          </div>
        </div>
      );
    }
    
    if (field === "VMS" && userType === "client") {
      const selected = fieldValues[field] || '';
      return (
        <Select
          value={selected}
          onChange={(e) => setFieldValues(prev => ({ ...prev, [field]: e.target.value }))}
          size="small"
          displayEmpty
          renderValue={(value) =>
            value ? (
              'Premium'
            ) : (
              <Typography component="span" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                Select VMS
              </Typography>
            )
          }
          sx={{
            width: '200px',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#3b82f6',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#3b82f6',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#3b82f6',
            },
          }}
        >
          <MenuItem value="premium">Premium</MenuItem>
        </Select>
      );
    }
    
    if (field === "RMS" && userType === "vendor") {
      const selected = fieldValues[field] || '';
      return (
        <Select
          value={selected}
          onChange={(e) => setFieldValues(prev => ({ ...prev, [field]: e.target.value }))}
          size="small"
          displayEmpty
          renderValue={(value) =>
            value ? (
              'Premium'
            ) : (
              <Typography component="span" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                Select RMS
              </Typography>
            )
          }
          sx={{
            width: '200px',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#3b82f6',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#3b82f6',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#3b82f6',
            },
          }}
        >
          <MenuItem value="premium">Premium</MenuItem>
        </Select>
      );
    }
    
    if (field === "Vendor Only Jobs" && userType === "client") {
      // If VMS is Premium+, show Unlimited
      if (fieldValues["VMS"] === "premiumplus") {
        return (
          <div className="flex justify-end items-center gap-2 w-2/3">
            <Typography sx={{ fontWeight: 'medium', color: '#059669', fontStyle: 'italic' }}>
              Unlimited
            </Typography>
          </div>
        );
      }
      
      // For other cases, allow manual input with auto-population
      return (
        <div className="flex justify-end items-center gap-2 w-2/3">
          <div className="flex items-center">
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleChange(field, -1)}
            >
              <Remove fontSize="small" />
            </Button>
            <input
              type="number"
              value={fieldValues[field] || 0}
              onChange={(e) => {
                const value = Math.max(0, parseInt(e.target.value) || 0);
                setFieldValues(prev => ({
                  ...prev,
                  [field]: value
                }));
              }}
              min="0"
              style={{
                width: '60px',
                textAlign: 'center',
                fontSize: '14px',
                padding: '4px'
              }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleChange(field, 1)}
            >
              <Add fontSize="small" />
            </Button>
          </div>
        </div>
      );
    }
    
    // Default for number input fields
    return (
      <div className="flex justify-end items-center gap-2 w-2/3">
        <div className="flex items-center">
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleChange(field, -1)}
          >
            <Remove fontSize="small" />
          </Button>
          <input
            type="number"
            value={fieldValues[field] || 0}
            onChange={(e) => {
              const value = Math.max(0, parseInt(e.target.value) || 0);
              setFieldValues(prev => ({
                ...prev,
                [field]: value
              }));
            }}
            min="0"
            style={{
              width: '60px',
              textAlign: 'center',
              fontSize: '14px',
              padding: '4px'
            }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleChange(field, 1)}
          >
            <Add fontSize="small" />
          </Button>
        </div>
      </div>
    );
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 650,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
    maxHeight: '90vh',
    overflow: 'auto'
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="usage-modal-title"
    >
      <Box sx={modalStyle}>
        <Typography id="usage-modal-title" variant="h6" component="h2" sx={{ mb: 2, fontWeight: 'bold', textAlign: 'center' }}>
          {initialUsage ? 'Update Usage' : 'Add Usage'}
        </Typography>
  
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <Typography>Loading...</Typography>
          </Box>
        ) : (
          <>
            {fields.map((field, index) => (
              <Box key={index} sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                mb: 2,
                p: 2,
                borderRadius: 1,
                bgcolor: '#f8fafc'
              }}>
                <Typography sx={{ fontWeight: 'medium', color: '#374151', width: '33%' }}>
                  {field}
                </Typography>
                {renderFieldControl(field)}
              </Box>
            ))}
          
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
              <Button 
                variant="outlined" 
                onClick={handleClose}
                sx={{ borderColor: '#3b82f6', color: '#3b82f6' }}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                onClick={handleSubmit}
                disabled={loading}
                sx={{ bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
              >
                {initialUsage ? 'Update Usage' : 'Add Usage'}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default UsageModal;